import { useCallback, useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'

type UseWritingSamplePdfLoaderOptions = {
  fallbackFileName: string
  loadContent: () => Promise<{ blob: Blob; fileName: string | null }>
  sentryContext?: Record<string, unknown>
}

type UseWritingSamplePdfLoaderReturn = {
  loading: boolean
  error: boolean
  pdfFile: File | null
  fileName: string | null
  retry: () => void
}

async function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') {
    return blob.text()
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '')
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  })
}

/**
 * Trellis often returns writing sample PDFs as octet-stream, so accept that
 * content type only after checking the file signature.
 */
async function isAcceptablePdfBlob(blob: Blob): Promise<boolean> {
  const normalizedBlobType = blob.type.split(';', 1)[0].trim().toLowerCase()
  if (normalizedBlobType === 'application/pdf') {
    return true
  }

  if (normalizedBlobType !== '' && normalizedBlobType !== 'application/octet-stream') {
    return false
  }

  return (await readBlobText(blob.slice(0, 5))) === '%PDF-'
}

export function useWritingSamplePdfLoader(options: UseWritingSamplePdfLoaderOptions): UseWritingSamplePdfLoaderReturn {
  const { fallbackFileName, loadContent, sentryContext = {} } = options
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [refetchCount, setRefetchCount] = useState(0)

  const sentryContextString = useMemo(() => JSON.stringify(sentryContext), [sentryContext])
  const sentryContextSafe = useMemo<Record<string, unknown>>(() => {
    try {
      return JSON.parse(sentryContextString) as Record<string, unknown>
    } catch {
      return {}
    }
  }, [sentryContextString])

  useEffect(() => {
    let cancelled = false

    async function fetchPdf() {
      setLoading(true)
      setError(false)
      setPdfFile(null)
      setFileName(null)

      try {
        const content = await loadContent()
        if (cancelled) return

        const { blob } = content
        const resolvedFileName = content.fileName || fallbackFileName
        const blobType = blob.type
        const isAcceptable = await isAcceptablePdfBlob(blob)
        if (cancelled) return

        if (!isAcceptable) {
          Sentry.captureException(new Error('Writing sample PDF blob has an unsupported MIME type'), {
            extra: { ...sentryContextSafe, blobType, fileName: resolvedFileName },
          })
          setError(true)
          return
        }

        setFileName(content.fileName)
        setPdfFile(new File([blob], resolvedFileName, { type: 'application/pdf' }))
      } catch (e) {
        if (cancelled) return

        console.error('Error loading writing sample PDF blob:', e)
        Sentry.captureException(e, { extra: { ...sentryContextSafe, fileName: fallbackFileName } })
        setError(true)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void fetchPdf()

    return () => {
      cancelled = true
    }
  }, [fallbackFileName, loadContent, sentryContextSafe, refetchCount])

  const retry = useCallback(() => {
    setRefetchCount((prev) => prev + 1)
  }, [])

  return { loading, error, pdfFile, fileName, retry }
}
